import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Product from "./Product";
import ProductSummary from "./ProductsSummary";
import {
  createEmptyApplication,
  getApplicationContent,
  updateApplicationContent,
  createEmptyProductData,
  createEmptySummary,
  getApplicationMetadata,
  getPreviousVersionContent,
  generateGUID,
  prepareProductsImport,
} from "../../services/Application";
import { createEmptyFileData } from "../../services/Application";
import InfoPopup from "./InfoPopup";
import { clientCanEdit, operatorCanEdit } from "./ReadOnly";
import ReadonlyOverlay from "./ReadonlyOverlay";
import CardHeader from "./CardHeader";
import {
  validateProductName,
  validateStep2,
  validateTariffCode,
} from "../../utils/Validators";
import {
  fileFormatGroups,
  hasAllowedFileExtension,
} from "../../utils/FileValidation";
import config from "../../config";
import {
  requiresIsztarCorrection,
  validateIsztarCode,
} from "../../services/IsztarValidation";
import logger from "../../utils/logger";

const getTariffValidationKey = (product, index) =>
  product?.id || `product-${index}`;

const ApplicationStep2 = ({ registerCallback, warningsCallback, operator }) => {
  const { accessToken, checkPermission, userId } = useAuth();
  const accessTokenRef = useRef(accessToken);
  const hasAccessToken = Boolean(accessToken);
  let { step, id } = useParams();
  let navigate = useNavigate();
  const location = useLocation();
  const importFileInputRef = useRef(null);

  const [application, setApplication] = useState(createEmptyApplication());
  const [applicant, setApplicant] = useState({
    producerName: "",
    producerCountry: "",
    producerStreet: "",
    producerBuilding: "",
    producerAppartment: "",
    producerPostCode: "",
    producerCity: "",
  });
  const [products, setProducts] = useState(application.Step_2.Data.Products);
  const [prevProducts, setPrevProducts] = useState([]);
  const [productsSummary, setProductsSummary] = useState(createEmptySummary);
  const [prevProductsSummary, setPrevProductsSummary] =
    useState(createEmptySummary());
  const [exportOnly, setExportOnly] = useState(false);
  const [infoPopupTrigger, setInfoPopupTrigger] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);

  const [warningPopupTrigger, setWarningPopupTrigger] = useState(0);
  const [errorDescription, setErrorDescription] = useState({
    Errors: [],
    Disclaimer: "",
  });
  const [allowForwardOnError, setAllowForwardOnError] = useState(false);

  const [validators, setValidators] = useState({});
  const [tariffValidations, setTariffValidations] = useState({});
  const [tariffValidationLoading, setTariffValidationLoading] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState("");

  const [status, setStatus] = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  const debug = config.runmode.debug;
  const isztarValidationEnabled =
    config.isztarValidation?.enabled === true;

  const productRefs = useRef([]);
  const productsRef = useRef(products);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const normalizeProductName = (value) =>
    (value ?? "").toString().trim().replace(/\s+/g, " ").toLowerCase();

  const resetScrollPosition = () => {
    if (typeof document !== "undefined") {
      document.querySelectorAll(".content, .main-content").forEach((element) => {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  const duplicateNameCounts = useMemo(() => {
    const counts = new Map();
    products.forEach((product) => {
      const normalized = normalizeProductName(product.productName);
      if (!normalized) return;
      counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });
    return counts;
  }, [products]);

  const getDuplicateNameCount = (productName) => {
    const normalized = normalizeProductName(productName);
    if (!normalized) return 0;
    return duplicateNameCounts.get(normalized) || 0;
  };

  const getApplicationNavigationSuffix = () => {
    const params = new URLSearchParams(location.search || "");
    params.set("view", "application");
    params.delete("focus");
    const search = params.toString();
    return `${search ? `?${search}` : ""}${location.hash || ""}`;
  };

  const handleForwardNavigation = () => {
    resetScrollPosition();
    navigate(
      "/application/edit/" +
        (parseInt(step) + 1) +
        "/" +
        id +
        getApplicationNavigationSuffix(),
      {
        replace: true,
      },
    );
  };

  const setValidator = (name, validator) => {
    setValidators((prevValidators) => {
      return {
        ...prevValidators,
        [name]: validator,
      };
    });
  };

  const removeValidator = (name) => {
    setValidators((prevValidators) => {
      const updatedValidators = { ...prevValidators };
      delete updatedValidators[name];
      return updatedValidators;
    });
  };

  const clearTariffValidation = (product, index) => {
    const key = getTariffValidationKey(product, index);
    setTariffValidations((previous) => {
      if (!(key in previous)) return previous;
      const updated = { ...previous };
      delete updated[key];
      return updated;
    });
  };

  const handleProductDataChange = (index, name, value) => {
    if (name === "productName" || name === "tariffCode") {
      clearTariffValidation(products[index], index);
    }
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      updatedProducts[index] = {
        ...updatedProducts[index],
        [name]: value,
      };
      return updatedProducts;
    });
  };

  const handleRemoveProduct = (indexToRemove) => {
    const removedProduct = products[indexToRemove];
    const validationKey = getTariffValidationKey(
      removedProduct,
      indexToRemove,
    );
    setProducts((prevProducts) =>
      prevProducts.filter((product, index) => index !== indexToRemove),
    );
    setTariffValidations((previous) => {
      const updated = { ...previous };
      delete updated[validationKey];
      return updated;
    });
    setTariffValidationLoading((previous) => {
      const updated = { ...previous };
      delete updated[validationKey];
      return updated;
    });
    removeValidator("product_" + indexToRemove);
  };

  const setApplicationMode = async (metadata = null) => {
    const resolvedMetadata =
      metadata ?? (await getApplicationMetadata(id, accessToken));

    if (!resolvedMetadata) {
      if (debug)
        logger.warn(
          "Brak metadanych wniosku. Pozostawiam tryb edycji bez zmian.",
        );
      return;
    }

    const statusId = Number(resolvedMetadata.status);
    const normalizedStatus = Number.isNaN(statusId)
      ? resolvedMetadata.status
      : statusId;

    setStatus(normalizedStatus);

    const isApplicant = resolvedMetadata.Applicant?.id === userId;
    const useOperatorRules =
      checkPermission("Obsługa wniosków") && !isApplicant;
    const canEdit = useOperatorRules
      ? operatorCanEdit(normalizedStatus)
      : clientCanEdit(normalizedStatus);

    setReadOnly(!canEdit);
  };

  const findMatchingPreviousProduct = (product) => {
    let prevProduct = prevProducts.find(
      (prevProduct) => prevProduct.id === product.id,
    );
    if (prevProduct) {
      return prevProduct;
    }
    return {};
  };

  useEffect(() => {
    if (!hasAccessToken) return;
    const fetchData = async () => {
      const token = accessTokenRef.current;
      if (!token) return;

      try {
        if (debug) logger.log("Pobieranie danych");
        const response = await getApplicationContent(id, token);
        const prevApplication = await getPreviousVersionContent(
          id,
          token,
        );

        if (debug) logger.log(response.Step_2);
        if (response.Step_2) {
          let productsToSet = response.Step_2.Data.Products || [];

          if (
            response.Step_1 &&
            response.Step_1.Data.exporterIsNotProducer === "true"
          ) {
            productsToSet = productsToSet.map((product) => {
              if (
                product.producer === "true" ||
                (product.producer !== "false" && product.exporter !== "true")
              ) {
                return {
                  ...product,
                  producer: "false",
                  exporter: "true",
                  producerName: "",
                  producerCountry: "",
                  producerStreet: "",
                  producerBuilding: "",
                  producerAppartment: "",
                  producerPostCode: "",
                  producerCity: "",
                  boughtInEU: "",
                  boughtOutsideEU: "",
                  importClearanceFile: createEmptyFileData(),
                };
              }
              return product;
            });
          }

          setProducts(productsToSet);
          setProductsSummary(response.Step_2.Data.Summary || {});
          setApplication(response);

          if (response.Step_1) {
            setApplicant({
              producerName: response.Step_1.Data.applicantName,
              producerCountry: "PL",
              producerStreet: response.Step_1.Data.applicantStreet,
              producerBuilding: response.Step_1.Data.applicantBuilding,
              producerAppartment: response.Step_1.Data.applicantAppartment,
              producerPostCode: response.Step_1.Data.applicantPostCode,
              producerCity: response.Step_1.Data.applicantCity,
            });
            setExportOnly(response.Step_1.Data.exporterIsNotProducer);
          }
        }

        if (prevApplication && prevApplication.Step_2) {
          setPrevProducts(prevApplication.Step_2.Data.Products || []);
          setPrevProductsSummary(prevApplication.Step_2.Data.Summary || {});
        }

        const metadata = await getApplicationMetadata(id, token);
        await setApplicationMode(metadata);
      } catch (error) {
        logger.error("Failed to fetch application content:", error);
      }
    };

    fetchData();
  }, [hasAccessToken, id]);
  useEffect(() => {
    updateProductsSummaries();
  }, [
    products,
    productsSummary.editSummaries,
    productsSummary.totalQuantity,
    productsSummary.totalNetWeight,
    productsSummary.totalGrossWeight,
  ]);

  const countWarnings = () => {
    let count = 0;
    for (const key in validators) {
      if (
        validators.hasOwnProperty(key) &&
        (validators[key] === "true" || validators[key] === true)
      ) {
        count++;
      }
    }
    return count;
  };

  useEffect(() => {
    warningsCallback(1, countWarnings());
  }, [validators]);

  const updateProductsSummaries = () => {
    if (
      productsSummary.editSummaries === "true" ||
      productsSummary.editSummaries === true
    )
      return;
    let newProductsSummaryEdit = "";
    let newQuantitySummaryEdit = "";
    let i = 1;
    products.forEach((product) => {
      //if (debug) logger.log("PN: " + product.productName);
      newProductsSummaryEdit +=
        i + ". " + product.productName + " (" + product.originCountry + ")\n";
      newQuantitySummaryEdit +=
        product.quantity +
        " " +
        (product.unit ? product.unit : "") +
        (product.quantityDesc ? " " + product.quantityDesc : "") +
        "\n";
      i++;
    });

    if (
      productsSummary.totalQuantity &&
      productsSummary.totalQuantity.length > 0
    )
      newQuantitySummaryEdit +=
        "Total: " + productsSummary.totalQuantity + "\n";
    if (
      productsSummary.totalNetWeight &&
      productsSummary.totalNetWeight.length > 0
    )
      newQuantitySummaryEdit +=
        "Total net weight: " + productsSummary.totalNetWeight + "\n";
    if (
      productsSummary.totalGrossWeight &&
      productsSummary.totalGrossWeight.length > 0
    )
      newQuantitySummaryEdit +=
        "Total gross weight: " + productsSummary.totalGrossWeight + "\n";

    handleProductSummaryDataChange(
      "productsSummaryEdit",
      newProductsSummaryEdit,
    );
    handleProductSummaryDataChange(
      "quantitySummaryEdit",
      newQuantitySummaryEdit,
    );
  };

  const handleProductSummaryDataChange = (name, value) => {
    //if (debug) logger.log("hPSDC: [" + name + "]: " + value)
    setProductsSummary((prevSummary) => {
      return {
        ...prevSummary,
        [name]: value,
      };
    });
  };

  const handleAddProduct = () => {
    // Funkcja obsługująca dodawanie nowego produktu
    setProducts((prevProducts) => [...prevProducts, createEmptyProductData()]);
  };

  // Funkcja klonująca produkt i wstawiająca go na pozycji poniżej klonowanego
  const handleCloneProduct = (index) => {
    const clonedProduct = { ...products[index] };
    clonedProduct.productName = clonedProduct.productName + " (kopia)";
    clonedProduct.id = generateGUID();
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      updatedProducts.splice(index + 1, 0, clonedProduct);
      return updatedProducts;
    });

    setTimeout(() => {
      const clonedProductRef = productRefs.current[index + 1];
      if (clonedProductRef) {
        clonedProductRef.scrollIntoView({ behavior: "smooth" });
      }
    }, 500);
  };

  const validateTariffProduct = async (product, index) => {
    if (!isztarValidationEnabled) return null;

    const key = getTariffValidationKey(product, index);
    const code = (product?.tariffCode || "").trim();
    const description = (product?.productName || "").trim();
    if (!validateTariffCode(code) || !validateProductName(description)) {
      clearTariffValidation(product, index);
      return null;
    }

    setTariffValidationLoading((previous) => ({
      ...previous,
      [key]: true,
    }));

    let validation;
    try {
      validation = await validateIsztarCode(
        code,
        description,
        accessTokenRef.current,
      );
    } catch (error) {
      validation = {
        error: error.message,
      };
    }

    const currentProduct = productsRef.current.find(
      (candidate, currentIndex) =>
        getTariffValidationKey(candidate, currentIndex) === key,
    );
    const stillCurrent =
      currentProduct &&
      (currentProduct.tariffCode || "").trim() === code &&
      (currentProduct.productName || "").trim() === description;

    if (stillCurrent) {
      setTariffValidations((previous) => ({
        ...previous,
        [key]: validation,
      }));
    }
    setTariffValidationLoading((previous) => {
      const updated = { ...previous };
      delete updated[key];
      return updated;
    });

    return {
      index,
      product: { ...product, tariffCode: code, productName: description },
      validation,
    };
  };

  const validateAllTariffCodes = async (productsToValidate) => {
    if (!isztarValidationEnabled) return [];

    const validations = await Promise.all(
      productsToValidate.map((product, index) =>
        validateTariffProduct(product, index),
      ),
    );
    return validations.filter(Boolean);
  };

  const validateData = async (application, e, aiValidations = []) => {
    const errorDescription = { Errors: [], Disclaimer: "" };

    const aiValidationInfo = aiValidations
      .filter(({ validation }) => requiresIsztarCorrection(validation))
      .map(({ product, validation }) => {
        const productLabel = product.productName
          ? ` produktu „${product.productName}”`
          : " produktu";
        return `Kod taryfy celnej${productLabel} (${product.tariffCode}) wymaga weryfikacji: ${validation.result_message}`;
      });
    let validationInfo = [
      ...validateStep2(application.Step_2.Data),
      ...aiValidationInfo,
    ];
    const hasNoProducts =
      !Array.isArray(application.Step_2?.Data?.Products) ||
      application.Step_2.Data.Products.length === 0;
    const hasDuplicateNames = Array.from(duplicateNameCounts.values()).some(
      (count) => count > 1,
    );

    if (validationInfo.length > 0) {
      errorDescription.Errors.push({
        Header: `Na bieżącej stronie wniosku stwierdzono następujące błędy (${validationInfo.length}):`,
        Description: validationInfo,
      });
      errorDescription.Disclaimer =
        "Dane wprowadzone we wniosku wymagają poprawy. Możesz kontynuować wypełnianie wniosku, jednak przed jego wysłaniem konieczna będzie korekta.";
      setWarningPopupTrigger(warningPopupTrigger + 1);
    }
    setErrorDescription(errorDescription);

    warningsCallback(1, validationInfo.length);
    if (e.target.name !== "forward") setAllowForwardOnError(false);
    if (hasDuplicateNames) setAllowForwardOnError(false);
    if (hasNoProducts) setAllowForwardOnError(false);
    if (validationInfo.length === 0 && e.target.name === "forward")
      handleForwardNavigation();
  };

  const handleSave = async (e) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const productDataJSON = products.map((product) => ({
        id: product.id || generateGUID(),
        productName: product.productName,
        quantity: product.quantity,
        unit: product.unit,
        quantityDesc: product.quantityDesc,
        tariffCode: product.tariffCode,
        originCountry: product.originCountry,
        producerName: product.producerName,
        producerCountry: product.producerCountry,
        producerStreet: product.producerStreet,
        producerBuilding: product.producerBuilding,
        producerAppartment: product.producerAppartment,
        producerPostCode: product.producerPostCode,
        producerCity: product.producerCity,
        producer: product.producer,
        exporter: product.exporter,
        boughtInEU: product.boughtInEU,
        boughtOutsideEU: product.boughtOutsideEU,
        importClearanceFile: product.importClearanceFile,
      }));

      const productsSummaryJSON = {
        totalQuantity: productsSummary.totalQuantity,
        totalNetWeight: productsSummary.totalNetWeight,
        totalGrossWeight: productsSummary.totalGrossWeight,
        countriesOfOrigin: productsSummary.countriesOfOrigin,
        transportDetails: productsSummary.transportDetails,
        productsRemarks: productsSummary.productsRemarks,
        productsSummaryEdit: productsSummary.productsSummaryEdit,
        quantitySummaryEdit: productsSummary.quantitySummaryEdit,
        editCountriesOfOrigin: productsSummary.editCountriesOfOrigin,
        editSummaries: productsSummary.editSummaries,
      };

      let app = { ...application };
      app.Step_2.Data.Products = productDataJSON;
      app.Step_2.Data.Summary = productsSummaryJSON;
      const validProductNames = productDataJSON.map((p) => p.productName);

      // Filtrowanie produktów w procesach z kroku 3
      if (app.Step_3?.Data?.Processes) {
        app.Step_3.Data.Processes = app.Step_3.Data.Processes.map((process) => ({
          ...process,
          Products: (process.Products || []).filter((prod) => {
            const matchedProduct = productDataJSON.find(
              (p) => p.productName === prod.name,
            );
            return (
              validProductNames.includes(prod.name) &&
              matchedProduct &&
              matchedProduct.producer !== false &&
              matchedProduct.producer !== "false"
            );
          }),
        }));
      }

      await updateApplicationContent(id, app, userId, accessToken);

      const aiValidations = await validateAllTariffCodes(productDataJSON);
      await validateData(app, e, aiValidations);

      setInfoPopupTrigger(infoPopupTrigger + 1);
    } finally {
      setIsSaving(false);
    }
  };

  const handleForward = async (e) => {
    e.preventDefault();
    setAllowForwardOnError(true);
    handleSave(e);
  };

  const handleBack = async (e) => {
    e.preventDefault();
    resetScrollPosition();
    navigate(
      "/application/edit/" +
        (parseInt(step) - 1) +
        "/" +
        id +
        getApplicationNavigationSuffix(),
      {
        replace: true,
      },
    );
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !hasAllowedFileExtension(file, fileFormatGroups.xlsx.extensions)) {
      e.target.value = "";
      setImportFile(null);
      setImportFileName("");
      setErrorDescription({
        Errors: [
          {
            Header: "Nieprawidłowy format pliku",
            Description: [fileFormatGroups.xlsx.label],
          },
        ],
        Disclaimer: "",
      });
      setWarningPopupTrigger((current) => current + 1);
      return;
    }
    if (file) {
      setImportFile(file);
      setImportFileName(file.name);
    }
  };

  const normalizeExcelValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
      if (!Number.isFinite(value)) return "";
      return Number.isInteger(value) ? String(value) : String(value);
    }
    const trimmed = String(value).trim();
    if (trimmed === "") return "";
    if (/^-?\d+(\.0+)$/.test(trimmed)) {
      return trimmed.replace(/\.0+$/, "");
    }
    return trimmed;
  };

  const handleImport = async () => {
    if (!importFile) return;
    let importedProducts = await prepareProductsImport(importFile, accessToken);
    if (importedProducts && Array.isArray(importedProducts.products)) {
      const mappedProducts = importedProducts.products.map((product) => ({
        id: generateGUID(),
        productName: product.productName || "",
        quantity: normalizeExcelValue(product.quantity),
        unit: product.unit || "",
        quantityDesc: product.quantityDesc || "",
        tariffCode: normalizeExcelValue(product.tariffCode),
        originCountry: product.originCountry || "",
        producerName: product.producerName || "",
        producerCountry: product.producerCountry || "",
        producerStreet: product.producerStreet || "",
        producerBuilding: product.producerBuilding || "",
        producerAppartment: product.producerAppartment || "",
        producerPostCode: product.producerPostCode || "",
        producerCity: product.producerCity || "",
        producer: product.producer ? "true" : "false",
        exporter: product.producer ? "false" : "true",
        boughtInEU: product.boughtInEU ? "true" : "false",
        boughtOutsideEU: product.boughtInEU ? "false" : "true",
        importClearanceFile: createEmptyFileData(),
      }));
      setProducts(mappedProducts);
      setTariffValidations({});
      setTariffValidationLoading({});
      setShowImportModal(false);
    } else {
      alert("Błąd podczas importu produktów z pliku XLSX.");
    }
  };

  const handleButtonClick = () => {
    importFileInputRef.current.click();
  };

  // Skraca nazwę pliku do 30 znaków i dodaje "..."
  const getShortFileName = (name) => {
    if (!name) return "";
    return name.length > 30 ? name.slice(0, 30) + "..." : name;
  };

  return (
    <div className="pb-2">
      <CardHeader
        text="Produkty / Towary - dodawanie"
        commentId="2-1"
        commentVisible={status !== 1}
        registerCallback={registerCallback}
        viewAll={checkPermission("Obsługa wniosków")}
      />
      <div className="ms-4 w55">
        <div>
          W tej sekcji prosimy Cię o określenie które produkty wyprodukowałeś we
          własnej firmie, a które towary zakupiłeś od innych przedsiębiorców.
          Ponieważ w ramach jednej transakcji możesz eksportować produkty
          własnej produkcji oraz towary zakupione od innych przębiorców, w tej
          sekcji odrębnie podajesz dane dla produktów i towarów.
          <br />
          <strong>
            {" "}
            Pamiętaj, aby uzupełnić poszczególne pola w języku, w jakim mają się
            pojawić na świadectwie.
          </strong>
        </div>
        <div className="d-flex flex-column w47">
          <div></div>
          {products.length === 0 && (
            <div className="d-flex flex-row">
              <button
                type="button"
                className="btn btn-primary w11 h2 rounded-2 ms-auto"
                onClick={handleAddProduct}
              >
                Dodaj produkt
              </button>
            </div>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="card container-fluid shadow d-flex flex-row ms-4 mt-2 py-2 px-4 w55">
          <div className="d-flex flex-row w47">
            <div>Tutaj możesz zaimportować listę produktów z pliku XLSX.</div>
            <div className="ms-auto">
              <button
                type="button"
                className="btn btn-outline-primary w11 h2 rounded-2"
                onClick={() => setShowImportModal(true)}
              >
                Importuj z pliku XLSX
              </button>
            </div>
          </div>
        </div>
      )}

      {products.map((product, index) => {
        const previousData = findMatchingPreviousProduct(product);
        const isNewProduct =
          checkPermission("Obsługa wniosków") &&
          (!previousData ||
            Object.keys(previousData).length === 0 ||
            !previousData.id);
        const duplicateNameCount = getDuplicateNameCount(product.productName);
        const isDuplicateName = duplicateNameCount > 1;
        const tariffValidationKey = getTariffValidationKey(product, index);

        return (
          <div
            key={index}
            className={
              "card container-fluid shadow ms-4 mt-2 px-4 py-3 w55 " +
              (isNewProduct ? "border border-danger" : "")
            }
            ref={(el) => (productRefs.current[index] = el)}
          >
            {isNewProduct && (
              <div
                className="tooltip position-absolute"
                style={{ top: "0.5em", right: "0.5em" }}
              >
                <span className="bi bi-clock-history text-danger fs-4 z-3" />
                <span className="tooltiptext tooltip-top-right fs-6">
                  Nowy produkt
                </span>
              </div>
            )}
            <div className="d-flex flex-column w47 fade fade-in">
              <Product
                id={product.id}
                index={index}
                data={product}
                previousData={previousData}
                showPreviousData={checkPermission("Obsługa wniosków")}
                applicant={applicant}
                exportOnly={exportOnly}
                onDataChange={(name, value) =>
                  handleProductDataChange(index, name, value)
                }
                onRemove={() => handleRemoveProduct(index)}
                onClone={() => handleCloneProduct(index)}
                commentVisible={status !== 1}
                registerCallback={registerCallback}
                viewAll={checkPermission("Obsługa wniosków")}
                handleValidate={setValidator}
                readOnly={readOnly}
                operator={operator}
                isNewProduct={isNewProduct}
                isDuplicateName={isDuplicateName}
                duplicateNameCount={duplicateNameCount}
                canRemove={products.length > 1}
                tariffValidation={tariffValidations[tariffValidationKey]}
                tariffValidationLoading={Boolean(
                  tariffValidationLoading[tariffValidationKey],
                )}
                isztarValidationEnabled={isztarValidationEnabled}
                onValidateTariffCode={() =>
                  validateTariffProduct(product, index)
                }
              />
            </div>
          </div>
        );
      })}

      {!readOnly && products.length > 0 && (
        <div className="d-flex flex-row ms-4 mt-2 w55">
          <button
            type="button"
            className="btn btn-primary w11 h2 rounded-2 ms-auto"
            onClick={handleAddProduct}
          >
            Dodaj kolejny produkt
          </button>
        </div>
      )}

      {productsSummary && prevProductsSummary && (
        <div>
          <CardHeader
            text="Podsumowanie"
            commentId="2-2"
            commentVisible={status !== 1}
            registerCallback={registerCallback}
            viewAll={checkPermission("Obsługa wniosków")}
          />
          <div className="card container-fluid shadow ms-4 px-4 py-3 w55">
            <div className="d-flex flex-column w47">
              <ReadonlyOverlay readonly={readOnly}>
                <ProductSummary
                  data={productsSummary}
                  previousData={prevProductsSummary}
                  showPreviousData={checkPermission("Obsługa wniosków")}
                  onDataChange={(name, value) =>
                    handleProductSummaryDataChange(name, value)
                  }
                  registerCallback={registerCallback}
                  operator={operator}
                />
              </ReadonlyOverlay>
            </div>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="flex-column mt-3">
          <form>
            <div className="d-flex flex-row ms-4 w55">
              <button
                className="btn btn-outline-primary me-auto w11 h2"
                onClick={handleBack}
              >
                Wstecz
              </button>

              <button
                type="button"
                name="save"
                className="btn btn-outline-primary w11 h2"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Sprawdzanie kodów…" : "Zapisz wersję roboczą"}
              </button>

              <button
                type="button"
                name="forward"
                className="btn btn-primary ms-1 w11 h2"
                onClick={handleForward}
                disabled={isSaving}
              >
                {isSaving ? "Sprawdzanie kodów…" : "Zapisz i przejdź dalej"}
              </button>
            </div>
          </form>
        </div>
      )}
      <InfoPopup
        title={"Zapisano wersję roboczą wniosku."}
        trigger={infoPopupTrigger}
      />
      <InfoPopup
        title={""}
        errorDescription={errorDescription}
        trigger={warningPopupTrigger}
        errorMode={true}
        actionOnAccept={handleForwardNavigation}
        allowForward={allowForwardOnError}
      />

      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content card d-flex flex-column w35 p-2">
            <div className="modal-header mb-1">
              <h4>Import listy produktów</h4>
            </div>
            <div className="flex-column mb-2">
              <div className="d-flex flex-row">
                <label className="form-label">
                  Możesz zaimportować listę produktów korzystając z gotowego
                  szablonu w formacie XLSX. W tym celu{" "}
                  <a href="/dokumenty/szablon_importu_co.xlsx" download>
                    pobierz szablon XLSX
                  </a>
                  , wypełnij go i wgraj za pomocą poniższego formularza.
                </label>
              </div>

              <div className="d-flex flex-row mt-2">
                <input
                  type="file"
                  onChange={handleImportFileChange}
                  id="fileInput"
                  ref={importFileInputRef}
                  style={{ display: "none" }}
                  accept={fileFormatGroups.xlsx.accept}
                />
                <div>
                  <button
                    className="btn btn-outline-primary rounded-end-0 ms-0 w7 h2 tooltip"
                    onClick={handleButtonClick}
                    title={`${fileFormatGroups.xlsx.label} Maksymalny rozmiar: 50 MB.`}
                  >
                    Wybierz plik
                    <span className="tooltiptext">
                      Dopuszczalny format: XLSX, max rozmiar 50 MB
                    </span>
                  </button>
                </div>
                <div className="flex-row border border-1 rounded-end-2 h2 w-100 tooltip">
                  <div className="ps-1 w-100">
                    <span className="fs-5 text-black">
                      {getShortFileName(importFileName)}
                    </span>
                  </div>
                  <span className="tooltiptext">
                    {importFileName ? importFileName : "Brak wybranego pliku"}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-primary w7 h2"
                onClick={handleCloseImportModal}
              >
                Anuluj
              </button>
              {importFileName ? (
                <button
                  type="button"
                  className="btn btn-primary w7 ms-1 h2"
                  onClick={handleImport}
                >
                  Importuj
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary w7 ms-1 h2"
                  disabled
                >
                  Importuj
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStep2;
